import { memo } from "react";
import { useI18n } from "@/i18n";
import { colors, spacing, typography, transitions } from "@/design-system/tokens";

const FEATURE_ICONS = [
  <svg key="inv" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>,
  <svg key="ord" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>,
  <svg key="ana" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>,
  <svg key="aut" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07M8.46 8.46a5 5 0 0 0 0 7.07" />
  </svg>,
];

export const Features = memo(function Features() {
  const { t } = useI18n();

  const items = [
    t.features.items.inventory,
    t.features.items.orders,
    t.features.items.analytics,
    t.features.items.automation,
  ];

  return (
    <section
      id="features"
      aria-label="Product features"
      style={{
        background: "#fff",
        padding: `${spacing["3xl"]} ${spacing.xl}`,
      }}
    >
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: spacing["3xl"],
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.lg, maxWidth: "640px" }}>
          <FeatureBadge label={t.features.badge} />
          <h2
            style={{
              fontFamily: typography.fontFamily,
              fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
              fontWeight: typography.weight.bold,
              color: "#111",
              margin: 0,
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
            }}
          >
            {t.features.title}
          </h2>
          <p
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.base,
              color: "#666",
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            {t.features.subtitle}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: spacing.lg,
          }}
          className="features-grid"
        >
          {items.map((item, i) => (
            <FeatureCard
              key={item.title}
              icon={FEATURE_ICONS[i]!}
              title={item.title}
              desc={item.desc}
            />
          ))}
        </div>
      </div>
    </section>
  );
});

const FeatureBadge = memo(function FeatureBadge({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: spacing.sm,
        background: "rgba(235,81,57,0.08)",
        border: `1px solid rgba(235,81,57,0.2)`,
        borderRadius: "9999px",
        padding: `${spacing.xs} ${spacing.md}`,
        width: "fit-content",
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
      <span
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.xs,
          fontWeight: typography.weight.semibold,
          color: colors.primary,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </span>
    </div>
  );
});

const FeatureCard = memo(function FeatureCard({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <article
      style={{
        background: "#fafafa",
        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: "16px",
        padding: spacing.xl,
        display: "flex",
        flexDirection: "column",
        gap: spacing.md,
        transition: `all ${transitions.base}`,
        cursor: "default",
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLElement;
        el.style.borderColor = `rgba(235,81,57,0.3)`;
        el.style.background = "#fff";
        el.style.transform = "translateY(-2px)";
        el.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLElement;
        el.style.borderColor = "rgba(0,0,0,0.07)";
        el.style.background = "#fafafa";
        el.style.transform = "translateY(0)";
        el.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          width: "44px",
          height: "44px",
          background: "rgba(235,81,57,0.1)",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.primary,
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <h3
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.lg,
          fontWeight: typography.weight.bold,
          color: "#111",
          margin: 0,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.sm,
          color: "#666",
          margin: 0,
          lineHeight: 1.7,
        }}
      >
        {desc}
      </p>
    </article>
  );
});
